import React from 'react';
import { Redirect, Route } from 'react-router-dom';
import lazyLoad from 'utils/lazyLoad';
import useFeatureToggle from 'hooks/useFeatureToggle';
import { getRouterPath } from 'utils/navigation';

import { removeTrailingSlashes } from './utils';
import useIncidentTypesRoutes from './useIncidentTypesRoutes';
import useIncidentAttachmentsRoutes from './useIncidentAttachmentsRoutes';

const DocumentLibrary = lazyLoad(
  () =>
    import(/* webpackChunkName: "DocumentLibrary" */ 'pages/DocumentLibrary'),
);
const DocumentLibraryPreview = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "DocumentLibrary" */ 'pages/DocumentLibrary/DocumentLibraryPreview'
    ),
);
const IncidentManagement = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "IncidentManagement" */ 'pages/IncidentManagement'
    ),
);
const IncidentDetails = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "IncidentDetails" */ 'pages/IncidentManagement/IncidentDetails'
    ),
);
const IncidentTaskLists = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "IncidentTaskLists" */ 'pages/IncidentManagement/IncidentTaskLists'
    ),
);
const PostIncidentReportsPage = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "PostIncidentReports" */ 'pages/PostIncidentReports'
    ),
);
const Playbooks = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "Playbooks" */ 'pages/IncidentManagement/Playbooks'
    ),
);
const PlaybookDetails = lazyLoad(
  () =>
    import(
      /* webpackChunkName: "PlaybookDetails" */ 'pages/IncidentManagement/Playbooks/PlaybookDetails'
    ),
);

/**
 * As of COR-68391, resource pages should specify a subpage location, formatted as:
 *   `/xmatters/ui/<resource>/<identifier>/<subpage>`
 *
 * Auto-redirect any older incidents links from `/incidents/:id` to a `/overview` subpage.
 */
const IncidentDetailsRedirect = ({ location }) => (
  <Redirect to={`${removeTrailingSlashes(location.pathname)}/overview`} />
);

export default function useIncidentsRoutes() {
  const isIncidentManagementMVPEnabled = useFeatureToggle(
    'INCIDENT_MANAGEMENT_MVP',
  );
  const isDocumentLibraryEnabled = useFeatureToggle('DOCUMENT_LIBRARY');
  const isIncidentTaskListsEnabled = useFeatureToggle('INCIDENT_TASK_LISTS');
  const incidentTypeRoutes = useIncidentTypesRoutes();
  const incidentAttachmentsRoutes = useIncidentAttachmentsRoutes();
  const isIncidentPlaybooksEnabled = useFeatureToggle('PLAYBOOKS_MVP');

  if (!isIncidentManagementMVPEnabled) {
    return [];
  }

  return [
    ...incidentTypeRoutes,
    ...incidentAttachmentsRoutes,
    ...(isIncidentPlaybooksEnabled
      ? [
          <Route
            path={getRouterPath('/incidents/playbooks/:playbookUuid')}
            component={PlaybookDetails}
          />,
          <Route
            path={getRouterPath('/incidents/playbooks')}
            component={Playbooks}
          />,
        ]
      : []),
    ...(isIncidentTaskListsEnabled
      ? [
          <Route
            exact
            path={getRouterPath('/incidents/incident-task-lists')}
            component={IncidentTaskLists}
          />,
          <Route
            exact
            path={getRouterPath('/incidents/incident-task-list/:taskListUuid')}
            component={IncidentTaskLists}
          />,
        ]
      : []),
    <Route
      exact
      path={getRouterPath('/incidents/:incidentUuid/overview')}
      component={IncidentDetails}
    />,
    <Route
      exact
      path={getRouterPath('/incidents/:incidentUuid')}
      component={IncidentDetailsRedirect}
    />,
    <Route
      exact
      path={getRouterPath('/incidents')}
      component={IncidentManagement}
    />,
    ...(isDocumentLibraryEnabled
      ? [
          <Route
            exact
            path={getRouterPath('/document-library')}
            component={DocumentLibrary}
          />,
          <Route
            exact
            path={getRouterPath('/document-library/:folderPath')}
            component={DocumentLibrary}
          />,
          <Route
            exact
            path={getRouterPath('/document-library/document/:documentId')}
            component={DocumentLibraryPreview}
          />,
        ]
      : []),
    <Route path={getRouterPath('/pirs')} component={PostIncidentReportsPage} />,
  ];
}
